// Teaser „Das Protokoll“: Bewegung und Warteliste. Übernommen aus relaunch/prototyp/assets/js/app.js.
// Alles ist ohne JavaScript lesbar. Bei reduzierter Bewegung und im Theme-Editor gibt es Standbilder (Klasse is-still im Head).
const root = document.documentElement;
const still = root.classList.contains('is-still');
const $ = (selector, scope = document) => scope.querySelector(selector);
const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

// Scroll-Fortschritt eines Elements: 0 = Oberkante erreicht start, 1 = Unterkante erreicht end (Anteile der Fensterhöhe).
const progress = (element, start = 1, end = 0) => {
  const rect = element.getBoundingClientRect();
  const from = innerHeight * start;
  const to = innerHeight * end - rect.height;
  return clamp((from - rect.top) / (from - to));
};
const onScroll = (callback) => {
  let queued = false;
  const run = () => { queued = false; callback(); };
  addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(run); } }, { passive: true });
  addEventListener('resize', run);
  run();
};

// Header: bekommt die Farbe der Fläche direkt unter ihm, die Marke folgt ihr (gemessen am linken Rand, dort liegt nie ein Bild).
const surfaceAt = (x, y, self) => {
  for (const element of document.elementsFromPoint(x, y)) {
    if (self?.contains(element)) continue;
    const color = getComputedStyle(element).backgroundColor;
    const [r, g, b, a = 1] = (color.match(/[\d.]+/g) ?? []).map(Number);
    if (r === undefined || a < .5) continue;
    return { color, dark: .2126 * r + .7152 * g + .0722 * b < 128 };
  }
  return { color: '', dark: false };
};
const header = $('[data-header]');
if (header) onScroll(() => {
  const surface = surfaceAt(2, header.getBoundingClientRect().bottom + 1, header);
  header.style.setProperty('--header-bg', surface.color || 'transparent');
  header.dataset.tone = surface.dark ? 'dark' : 'light';
});

// Einblenden beim Scrollen
if (!still && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    entry.target.classList.add('in');
    io.unobserve(entry.target);
  }), { rootMargin: '0px 0px -10% 0px' });
  $$('[data-reveal]').forEach((element) => {
    if (element.getBoundingClientRect().top < innerHeight * .9) element.classList.add('in');
    else io.observe(element);
  });
  root.classList.add('js-motion');
}

// Haltung: Wörter füllen sich mit dem Scrollen
$$('[data-words]').forEach((heading) => {
  if (still) return;
  const label = heading.textContent;
  const fragment = document.createDocumentFragment();
  label.split(/(\s+)/).forEach((part) => {
    if (!part) return;
    if (/^\s+$/.test(part)) { fragment.append(part); return; }
    const span = document.createElement('span');
    span.dataset.word = '';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = part;
    fragment.append(span);
  });
  heading.replaceChildren(fragment);
  heading.setAttribute('aria-label', label);
  const words = $$('[data-word]', heading);
  onScroll(() => {
    const lit = Math.round(progress(heading, .85, .45) * words.length);
    words.forEach((word, index) => word.classList.toggle('on', index < lit));
  });
});

// Stummer Loop im Hero: lädt kurz vor Sichtkontakt, spielt nur im Bild und läuft durch (kein Pause-Schalter, Wunsch Finn
// 03.11.2026). Bei reduzierter Bewegung bleibt das Standbild.
$$('[data-loop]').forEach((box) => {
  const video = $('video', box);
  if (still) return;
  let visible = false;
  const sync = () => {
    if (visible && !document.hidden) {
      if (!video.getAttribute('src')) {
        video.src = (matchMedia('(max-width: 767px)').matches && video.dataset.srcSmall) || video.dataset.src;
      }
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };
  new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); }, { threshold: .2 }).observe(box);
  document.addEventListener('visibilitychange', sync);
});

// Early Access: Anmeldung über Klaviyos Client-API mit dem öffentlichen Site-Key, wie assets/kodex-popup.js im Live-Theme.
// Die Liste verlangt Double-Opt-in. Die Profil-Eigenschaft trennt den Early Access von anderen Anmeldungen.
const KLAVIYO_REVISION = '2026-07-15';
const subscribe = (form, email) => fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(form.dataset.company)}`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/vnd.api+json',
    Accept: 'application/vnd.api+json',
    revision: KLAVIYO_REVISION,
  },
  body: JSON.stringify({
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Protokoll Early Access',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { quelle: 'protokoll-early-access', early_access_protokoll: true },
              subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: form.dataset.list } } },
    },
  }),
});

$$('[data-waitlist]').forEach((box) => {
  const form = $('form', box);
  const input = $('input[type="email"]', form);
  const button = $('button[type="submit"]', form);
  const error = $('[data-waitlist-error]', form);
  const done = $('[data-waitlist-done]', box);
  const fail = (message) => {
    error.textContent = message;
    error.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    input.focus();
  };
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    error.hidden = true;
    input.removeAttribute('aria-invalid');
    const email = input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { fail(form.dataset.errorMail); return; }
    // Fallenfeld: von Hand bleibt es leer, Bots füllen es aus
    if (form.elements.website?.value) return;
    button.disabled = true;
    try {
      const response = await subscribe(form, email);
      if (!response.ok) throw new Error(`Klaviyo-Anmeldung: ${response.status}`);
      // Beide Formulare der Seite zeigen danach die Bestätigung
      $$('[data-waitlist]').forEach((other) => {
        $('form', other).hidden = true;
        const message = $('[data-waitlist-done]', other);
        $('[data-waitlist-address]', message).textContent = email;
        message.hidden = false;
      });
      done.focus({ preventScroll: true });
    } catch (problem) {
      console.error(problem);
      fail(form.dataset.errorSend);
    } finally {
      button.disabled = false;
    }
  });
});
